import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock';
import { useCallback, useEffect, useState } from 'react';

import { useRouter } from '../';

export namespace UsePopup {
    export type Open = (component: PopupComponent) => void;

    export type Close = (historyChange?: boolean) => void;

    export type HandleEscapeKeyPress = (event: KeyboardEvent) => void;

    export type PopupComponent = React.ReactNode;
}

export const usePopup = () => {
    const [popupComponent, setPopupComponent] =
        useState<UsePopup.PopupComponent>();

    const { navigate, location } = useRouter();

    /**
     * Opens a popup with `component` rendered inside. On call, router's
     * `navigate` is used to make popup act like a separate route,
     * alowing user to use browser's back button to close popup.
     */
    const openPopup: UsePopup.Open = useCallback(
        (component) => {
            setPopupComponent(component);
            navigate(
                {
                    pathname: location.pathname,
                    search: location.search,
                },
                {
                    state: 'popup',
                },
            );
        },
        [navigate, location.pathname],
    );

    /**
     * The popup could be closed:
     * - directly: The user clicks the overlay or X button. We are using
     * `closePopup(true)` because we need to remove a history state that have
     * been added by `openPopup`.
     * - indirectly: The user uses native browser back button. This
     * is called with `closePopup(false) on window popstate` because
     * the navigation will take us to previous page anyway, there is no need
     * to go back twice.
     *
     * `historyChange` is used to determine if application should force
     * browser history to goBack() while closing a popup. The value is `true`
     * by default. This implementation allows us to handle following scenerios:
     *
     * - click outside the popup
     * - click on popup close buttons
     * - go back to previous page as a native browser back button
     */
    const closePopup: UsePopup.Close = useCallback(
        (historyChange = true) => {
            setPopupComponent(null);

            if (historyChange) {
                navigate(-1);
            }
        },
        [navigate],
    );

    /**
     * Allow to close popup by pressing `Escape` button on keyboard
     */
    const handleEscapeKeyPress: UsePopup.HandleEscapeKeyPress = useCallback(
        (event) => {
            if (event.code === 'Escape') {
                closePopup();
            }
        },
        [closePopup],
    );

    useEffect(() => {
        if (popupComponent) {
            document.addEventListener('keydown', handleEscapeKeyPress);
            window.addEventListener('popstate', () => closePopup(false));
            const element = document.querySelector(
                '[data-test="popup-overlay"]',
            );

            if (element) {
                disableBodyScroll(element);
            }
        }

        return () => {
            document.removeEventListener('keydown', handleEscapeKeyPress);
            window.removeEventListener('popstate', () => closePopup(false));
            clearAllBodyScrollLocks();
        };
    }, [closePopup, handleEscapeKeyPress, popupComponent]);

    return { closePopup, openPopup, popupComponent };
};
